import { useQuery } from '@apollo/client';
import {
  DismissableInlineMessage,
  FormattedDate,
  Icon,
  CustomSearch,
  DynamicResultTable,
  OpenSidebarLink,
  getPromotionTitle,
  renderPromotionValue,
  NoDataContentInTable,
  useFeatureToggle,
  useApplicationContext,
} from '@css-xp/base';
import {
  Maybe,
  OrderItemRequestType,
  OrderLineItem,
  EntitlementDetails,
  SortDirection,
  Order,
  FetchOrdersForEntitlementQueryVariables,
  FetchOrdersForEntitlementQuery,
  Promotion,
} from '@css-xp/graphtypes/src/generated-types-new';
import { DATE_TIME_FORMAT, TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { capitalize, lowerCase, memoize } from 'lodash';
import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { SIDEBAR } from '../../components/entitlement-page';
import { fetchOrdersForEntitlement } from '../../queries/fetch-orders-for-entitlement';
import {
  getDescriptor,
  getId,
  getOrderLineItemStatusAndType,
  groupOrderLineItems,
} from '@css-xp/base/entitlement-utils';
import { CancellationReasonCodes } from '@atlassian/cssxp-types-entitlement';
import './order-history-table.scss';
import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  NO_DETAILS_TO_SHOW,
  isAnnualTermedSubscription,
} from '@atlassian/cssxp-utils-entitlement';
import { isJCAProduct } from '@atlassian/cssxp-utils-product';
import { ENABLE_ANNUAL_TERMS } from '@atlassian/cssxp-utils-generic';
import { BillingFrequency } from '@atlassian/cssxp-ccp-types';
import FormattedDateTimeZone from '@css-xp/base/formatted/src/date-time-zone';

interface Props {
  id: string;
  transactionAccountId: EntitlementDetails['transactionAccountId'];
  nextInvoiceTimeAt: EntitlementDetails['nextInvoiceTimeAt'];
  baseProduct: EntitlementDetails['baseProduct'];
  productKey: string;
  entitlement: EntitlementDetails;
  showFilters?: boolean;
}

interface FormData {
  searchTerm: string;
}

export const emptyStateOrderProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
  testId: 'no-order-history-state',
};

export const sortOptions = {
  sortFields: ['createdDate', 'sourceSequence', 'id'],
  sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC, SortDirection.DESC],
};

const getPromotionDetailsMessage = (promotions: OrderLineItem['promotions'], isRemoved?: boolean) => {
  return promotions?.map((promotion: Maybe<Promotion>, index: number) => {
    return (
      <>
        <p key={index} className="n-300">
          {isRemoved ? 'Removed ' : ''}Discount: {getPromotionTitle(promotion, { handleEmptyTitle: true })},{' '}
          {renderPromotionValue(promotion, { showDuration: true })}
        </p>
      </>
    );
  });
};

// Adding Tiers info  on all orders
const getUnitsMessage = (quantity: OrderLineItem['quantity']) => {
  return quantity !== null ? ` - Tier/Qty: ${quantity}` : '';
};

const RenderDatePlacedWithInvoiceInfo = (
  order: Order,
  transactionAccountId: EntitlementDetails['transactionAccountId'],
  nextInvoiceTimeAt: EntitlementDetails['nextInvoiceTimeAt']
) => {
  const context = useApplicationContext();
  let currentTimeZone;
  if (context.timeZone) currentTimeZone = context.timeZone;
  let nextInvoice = <>Invoice will be generated soon.</>;
  if (nextInvoiceTimeAt) {
    nextInvoice = (
      <>
        Invoice will be generated by <FormattedDate date={nextInvoiceTimeAt} format={DATE_TIME_FORMAT} />. See{' '}
        <Link to={`${TRANSACTION_ACCOUNT_PATH_2}/${transactionAccountId}`}>
          {' '}
          Account details {'>'} Billing profiles
        </Link>{' '}
        for more details.
      </>
    );
  }
  return (
    <div>
      <OpenSidebarLink
        key={`order-history-row-date}`}
        sidebar={SIDEBAR.ORDER_DETAIL}
        payload={{
          order,
        }}
        sidebarOptions={{
          isOverlay: true,
          sidebarWidth: '600px',
        }}
        isSelected={false}
      >
        <p className="linkableText">
          <FormattedDateTimeZone date={order?.created} showTime timeZone={currentTimeZone} />
        </p>{' '}
      </OpenSidebarLink>
      {order.hasPendingInvoice ? (
        <DismissableInlineMessage
          position="auto"
          renderMessage={() => {
            return <div> {nextInvoice} </div>;
          }}
          renderTitle={(toggleMessage) => (
            <div className="hoverable-icon" onClick={toggleMessage} data-testid="inline-message">
              <Icon type="info" size="small" /> <span>Generating Invoice</span>
            </div>
          )}
        />
      ) : null}
    </div>
  );
};

const getHead = memoize(() => {
  const cells = [
    {
      content: 'Date placed',
      width: 20,
    },
    {
      content: 'Description',
      width: 25,
    },
    {
      content: 'Details',
      width: 35,
    },
    {
      content: 'Placed by',
      width: 20,
    },
  ];
  return {
    cells,
  };
});

const buildAuthorDisplay = (author?: string | null, order?: Order) => {
  if (!author) {
    if (order?.orderLineItems?.nodes?.[0]?.type === OrderItemRequestType.SYSTEM_CANCELLATION_ORDER) {
      return <>{'System'}</>;
    }
    return <NoDataContentInTable />;
  }
  return author;
};

export const OrderHistoryTable = ({
  id,
  transactionAccountId,
  nextInvoiceTimeAt,
  baseProduct,
  productKey,
  entitlement,
  showFilters = false,
}: Props) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [filterOptions, setFilterOptions] = useState<{ key: string; value: string }[]>([]);
  const [isEnabled] = useFeatureToggle();
  const { loading, data, refetch, error } = useQuery<
    FetchOrdersForEntitlementQuery,
    FetchOrdersForEntitlementQueryVariables
  >(fetchOrdersForEntitlement, {
    variables: {
      id,
      searchOptions: { first: rowsPerPage, sort: { ...sortOptions } },
      filterOptions: filterOptions,
    },
    nextFetchPolicy: 'cache-first',
    skip: !id,
  });
  const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
  // 'Seq2 - ' basename correction
  if (baseProduct && baseProduct?.indexOf('-') > -1) baseProduct = baseProduct.split('-')[1].trim();

  // variables to use for paging and populating our template
  const totalCount = data?.entitlementDetails?.orders?.totalCount ?? 0;
  const nodes = data?.entitlementDetails?.orders?.nodes ?? [];

  const onSearch = async (formData: FormData) => {
    const input = formData && formData.searchTerm;
    const currentFilterOptions = filterOptions.filter(({ key }) => key !== 'orderNumber');
    if (input) {
      currentFilterOptions.push({
        key: 'orderNumber',
        value: input,
      });
    }
    setFilterOptions(currentFilterOptions);
    refetch({
      searchOptions: {
        first: rowsPerPage,
        offset: 0,
      },
      filterOptions: currentFilterOptions,
    });
    setCurrentPage(1);
  };

  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: (newPage - 1) * rowsPerPage,
        },
        filterOptions,
      });
    },
    [rowsPerPage, filterOptions, refetch]
  );

  const handleRowsChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const createOrderMessage = memoize((order: Maybe<Order>, baseProduct: string) => {
    const hasLineItems = (order?.orderLineItems?.nodes?.length ?? -1) > 0;
    if (!hasLineItems) {
      return [
        {
          descriptions: <div>Not available</div>,
          details: <div>Order not available</div>,
        },
      ];
    }

    const groupedLineItems: Maybe<OrderLineItem>[] = groupOrderLineItems(order);

    const message = groupedLineItems?.map((lineItem: any, index) => {
      const promotions = lineItem?.promotions;
      const removedPromotions = lineItem?.removedPromotions;
      const quantity = lineItem?.quantity;
      const orderTransitionType = lineItem?.orderTransitionType;
      const salesTransitionType = lineItem?.salesTransitionType;
      const orderLineItemMessage = ` 
                        ${getOrderLineItemStatusAndType(
                          lineItem?.status,
                          lineItem?.type,
                          promotions,
                          lineItem?.orderLineItemsAccountModification,
                          false
                        )} 
                        ${lineItem?.parentEntitlementId ? 'additional allocations ' : ''} 
                        ${getDescriptor(lineItem, baseProduct)}
                        ${getId(lineItem)}${getUnitsMessage(quantity)}
                    `;
      const hasPromotions = promotions && promotions?.length;

      const descriptionMessage = `${getOrderLineItemStatusAndType(
        lineItem?.status,
        lineItem?.type,
        promotions,
        lineItem?.orderLineItemsAccountModification,
        true
      )}`;

      return {
        ...(!lineItem?.type?.includes('OF_FAMILY') && {
          details: (
            <div key={`line-item-${index}`}>
              {orderLineItemMessage}
              {hasPromotions && <>{getPromotionDetailsMessage(promotions)}</>}
              {removedPromotions?.length > 0 ? getPromotionDetailsMessage(removedPromotions, true) : null}
              {!isAnnualTermedSubscription(entitlement, isAnnualTermEnabled) && lineItem?.nextBillingAnchorTimestamp ? (
                <div className="n-300">New bill date has been set</div>
              ) : null}
              {isJCAProduct(productKey) && (
                <div>
                  <span className="n-300">
                    Skip trial:{' '}
                    {lineItem?.skipTrial === null || lineItem?.skipTrial === undefined
                      ? 'N/A'
                      : capitalize(lineItem?.skipTrial)}
                  </span>
                </div>
              )}
              {[OrderItemRequestType.AMENDMENT_ORDER, OrderItemRequestType.SYSTEM_AMENDMENT_ORDER].includes(
                lineItem.type
              ) &&
                lineItem?.pricingPlan?.billingFrequency === BillingFrequency.ANNUAL && (
                  <div>
                    <span className="n-300">{`Billing cycle = Annual ${
                      isAnnualTermedSubscription(entitlement, isAnnualTermEnabled) ? 'Termed' : ''
                    }`}</span>
                  </div>
                )}
              {lineItem.type === OrderItemRequestType.CANCELLATION_ORDER && lineItem?.reasonCode && (
                <p>
                  <span className="n-300">
                    Cancellation reason:{' '}
                    {CancellationReasonCodes[lineItem?.reasonCode as keyof typeof CancellationReasonCodes]}
                  </span>
                </p>
              )}
            </div>
          ),
        }),
        ...(lineItem.isDisplay && {
          descriptions: (
            <div>
              {`${capitalize(orderTransitionType)} ${
                ['upgrade', 'downgrade', 'renew'].includes(lowerCase(salesTransitionType))
                  ? lowerCase(salesTransitionType)
                  : salesTransitionType !== null ||
                    ['CREATION_ORDER', 'TRANSFER_IN_ORDER', 'TRANSITION_ORDER'].includes(lineItem.type)
                  ? lowerCase(descriptionMessage)
                  : ''
              }`}
            </div>
          ),
        }),
      };
    });
    return message;
  });

  const getColumnData = (order: Order) => {
    const orderMessage = createOrderMessage(order, baseProduct ?? '') ?? [];
    return [
      {
        content: RenderDatePlacedWithInvoiceInfo(order, transactionAccountId, nextInvoiceTimeAt),
      },
      {
        content: orderMessage.map(({ descriptions }) => descriptions),
      },

      {
        content: (
          <>
            {orderMessage.map(({ details }) => details)}
            {order?.metadataObj?.consentTaken && order.metadataObj.consentTaken === 'true' && (
              <div>Consent taken: Yes</div>
            )}
          </>
        ),
      },
      {
        content: <>{buildAuthorDisplay(order.contextSubject)}</>,
      },
    ];
  };

  const createRows = (): Array<any> => {
    //@ts-ignore
    const result = (nodes ?? []).map((order: Order) => {
      return {
        key: order.id,
        cells: getColumnData(order),
      };
    });
    return result;
  };

  return (
    <DynamicResultTable
      testId="order-history"
      headingText="Order history"
      head={getHead()}
      rows={createRows()}
      emptyStateProps={emptyStateOrderProp}
      isLoading={loading}
      totalCount={totalCount}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onSearch={onSearch}
      isPaginationEnabled={nodes && nodes.length > 0 && totalCount > DEFAULT_ROWS_PER_PAGE}
      onRowChange={handleRowsChange}
      showFilters={showFilters}
    >
      <CustomSearch placeholder={'Search order'} onSearch={onSearch} isLoading={loading} />{' '}
    </DynamicResultTable>
  );
};
